
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const quickLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('footer.aboutUs'), path: '/about' },
    { name: t('nav.services'), path: '/services' },
    { name: t('nav.portfolio'), path: '/portfolio' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  const portfolioCompanies = [
  { name: 'ALEBEL Store', desc: 'Luxury E-Commerce' },
  { name: 'ALEBEL Tech', desc: 'Technology & Digital Solutions' },
  { name: t('projects.energy.title'), desc: t('footer.batteryEnergy') },
  { name: 'ALEBEL Healthcare', desc: 'Healthcare & Pharmaceuticals' },
  { name: 'ALEBEL Racing', desc: 'Motorsport & Performance' },
  { name: t('projects.electronics.title'), desc: t('footer.iotSmart') },
  { name: 'ALEBEL Device', desc: 'Smart Devices & Electronics' },
  { name: 'ALEBEL R&D', desc: 'Research & Development' },
  { name: t('projects.construction.title'), desc: t('footer.infrastructure') }
];

  return (
    <footer className="relative pt-24 pb-8 overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-[#1A3A5C] to-charcoal animate-gradient"></div>
      
      {/* Moving Color Overlay */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/5 via-transparent to-gold/5 animate-gradient"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-gold/3 to-transparent animate-gradient" style={{ animationDelay: '3s' }}></div>
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 80%, #D4AF37 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
        }}></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 md:gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-8 cursor-pointer">
              <img 
                src="/alebel-logo-transbg.png" 
                alt="ALEBEL Investment" 
                className="h-24 md:h-28 w-auto"
              />
            </Link>
            <p className="text-white/70 mb-8 leading-relaxed text-sm md:text-base">
              {t('footer.description')}
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              <a href="https://www.linkedin.com/company/135304350/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 glass-icon rounded-lg flex items-center justify-center hover:bg-gold hover:text-charcoal text-white/80 transition-all duration-300 cursor-pointer">
                <i className="ri-linkedin-fill text-lg"></i>
              </a>
              <a href="https://x.com/alebel_in" target="_blank" rel="noopener noreferrer" className="w-10 h-10 glass-icon rounded-lg flex items-center justify-center hover:bg-gold hover:text-charcoal text-white/80 transition-all duration-300 cursor-pointer">
              <i className="ri-twitter-fill text-lg"></i>
              </a>
              <a href="https://www.instagram.com/alebel_investment?igsh=ZjBmYzE2MDlrOGUy" target="_blank" rel="noopener noreferrer" className="w-10 h-10 glass-icon rounded-lg flex items-center justify-center hover:bg-gold hover:text-charcoal text-white/80 transition-all duration-300 cursor-pointer">
              <i className="ri-instagram-fill text-lg"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h3 className="text-gold text-sm font-semibold tracking-widest uppercase mb-6">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/70 hover:text-gold transition-colors duration-300 cursor-pointer text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Portfolio Companies */}
          <div className="lg:col-span-3">
            <h3 className="text-gold text-sm font-semibold tracking-widest uppercase mb-6">
              {t('footer.portfolio')}
            </h3>
            <ul className="space-y-4">
              {portfolioCompanies.map((company, index) => (
                <li key={index}>
                  <div className="text-white text-sm font-medium mb-1">{company.name}</div>
                  <div className="text-white/50 text-xs">{company.desc}</div>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
<div className="lg:col-span-3">
  <h3 className="text-gold text-sm font-semibold tracking-widest uppercase mb-6">
    {t('footer.contact')}
  </h3>
  <ul className="space-y-4">

    {/* WhatsApp */}
    <li>
      <a href="https://wa.me/97154888408" target="_blank" rel="noopener noreferrer" className={`flex items-start gap-3 text-white/70 hover:text-gold transition-colors duration-300 cursor-pointer group ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="w-8 h-8 glass-icon rounded-lg flex items-center justify-center">
          <i className="ri-whatsapp-line text-gold text-sm"></i>
        </div>
        <div className={isRTL ? 'text-right' : ''}>
          <div className="text-xs text-white/50 mb-1">WhatsApp</div>
          <div className="text-sm" dir="ltr">+971 54 888 4088</div>
        </div>
      </a>
    </li>

    {/* Landline */}
    <li>
      <a href="tel:+97148886288" className={`flex items-start gap-3 text-white/70 hover:text-gold transition-colors duration-300 cursor-pointer group ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="w-8 h-8 glass-icon rounded-lg flex items-center justify-center">
          <i className="ri-phone-line text-gold text-sm"></i>
        </div>
        <div className={isRTL ? 'text-right' : ''}>
          <div className="text-xs text-white/50 mb-1">Landline</div>
          <div className="text-sm" dir="ltr">+971 4 888 6288</div>
        </div>
      </a>
    </li>

    {/* Email */}
    <li>
      <a href="mailto:info@alebel.com" className={`flex items-start gap-3 text-white/70 hover:text-gold transition-colors duration-300 cursor-pointer group ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="w-8 h-8 glass-icon rounded-lg flex items-center justify-center">
          <i className="ri-mail-line text-gold text-sm"></i>
        </div>
        <div className={isRTL ? 'text-right' : ''}>
          <div className="text-xs text-white/50 mb-1">{t('cta.email')}</div>
          <div className="text-sm">info@alebel.com</div>
        </div>
      </a>
    </li>

    {/* Location */}
<li>
  <div className={`flex items-start gap-3 text-white/70 relative ${isRTL ? 'flex-row-reverse' : ''}`}>
    <div className="w-8 h-8 glass-icon rounded-lg flex items-center justify-center flex-shrink-0">
      <i className="ri-map-pin-line text-gold text-sm"></i>
    </div>
    <div className={isRTL ? 'text-right' : ''}>
      <div className="text-xs text-white/50 mb-1">{t('cta.location')}</div>
      <div className="text-sm">{isRTL ? 'دبي، الإمارات العربية المتحدة' : 'Dubai, United Arab Emirates'}</div>
      <div className="text-xs text-white/40 mt-1">{isRTL ? 'مكتب 501، مبنى ناسكو، صلاح الدين' : 'Office 501, Nasco Building, Salah Al Din'}</div>
    </div>
  </div>
</li>

  </ul>
</div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-xs md:text-sm">
            © 2026 {isRTL ? 'الأبل للاستثمار. جميع الحقوق محفوظة.' : 'ALEBEL Investment. All rights reserved.'}
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a href="#" className="text-white/50 hover:text-gold text-xs md:text-sm transition-colors duration-300 cursor-pointer">
              {t('footer.privacy')}
            </a>
            <a href="#" className="text-white/50 hover:text-gold text-xs md:text-sm transition-colors duration-300 cursor-pointer">
              {t('footer.terms')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
